import { FormEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import {
  Button,
  Card,
  Typography,
  useAlert,
  useInput
} from '@destination/components';

import uploadModel from '@api/upload-model';
import { isValidJson } from '@utils/input-utils';

type IAlert = Parameters<ReturnType<typeof useAlert>['notify']>[0];

export default function UploadForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitBtnRef = useRef<HTMLButtonElement>(null);

  const navigate = useNavigate();

  const { t } = useTranslation(undefined, {
    keyPrefix: 'upload_model.upload_form'
  });

  const { notify } = useAlert();

  const { value, isValid, isTouched, valueChangeHandler } =
    useInput(isValidJson);

  const handlePrettify = () => {
    const prettified = JSON.stringify(JSON.parse(value), null, 4);
    valueChangeHandler(prettified);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);

    const payload = JSON.parse(value);

    const { error } = await uploadModel(payload);

    const alert: IAlert = {
      header: t('success_message'),
      variant: 'success'
    };

    if (error) {
      alert.header = t('error_message');
      alert.variant = 'error';
    }

    notify(alert);
    setIsSubmitting(false);
    navigate('/models');
  };

  const isDisabled = isTouched && isValid;

  return (
    <section className="mt-4">
      <Typography variant="sectionTitle">{t('title')}</Typography>
      <Card data-testid="model-upload-form" className="mt-2 max-w-lg">
        <form onSubmit={handleSubmit}>
          <div className="flex justify-between items-center">
            <label htmlFor="model-json">{t('json_schema_label')}</label>
            <Button
              variant="ghost"
              type="button"
              onClick={handlePrettify}
              disabled={!isDisabled}
            >
              {t('prettify')}
            </Button>
          </div>
          <textarea
            id="model-json"
            className={`border-[#BABABA] border rounded p-2 outline-none block w-full mt-2 ${
              !isValid ? 'border-red-500' : ''
            }`}
            rows={10}
            value={value}
            onChange={event => valueChangeHandler(event.target.value)}
          ></textarea>
          {!isValid && <p className="text-red-500">{t('invalid_json')}</p>}
          <button ref={submitBtnRef} hidden></button>
        </form>
      </Card>
      <div data-testid="actions" className="flex gap-4 justify-center mt-64">
        <Link to="/models">
          <Button variant="ghost" disabled={isSubmitting}>
            {t('cancel')}
          </Button>
        </Link>
        <Button
          disabled={!isDisabled}
          isLoading={isSubmitting}
          onClick={() => submitBtnRef.current?.click()}
        >
          {t('submit')}
        </Button>
      </div>
    </section>
  );
}
